import { ProcessGeneral } from 'api/endpoints/processes';
import { UPLOAD_FILE_FIELD_NAME } from 'constants/global';

import BaseRepo from '../../../BaseRepo';
import { buildBadRequestApiError } from '../../../helpers/errors';
import { ApiResponseAsync, isApiError } from '../../../models';
import { SearchRequestTyped } from '../../../search';
import {
  BarcodeImporterBatch,
  BarcodeImporterLog,
  BarcodeImporterProcessInstance,
  DailyBarcodeImporterBatches,
  IBarcodeImporterProcessingRepo,
  SearchableBarcodeImporterLog,
} from './IBarcodeImporterProcessingRepo';

export class BarcodeImporterProcessingRepo
  extends BaseRepo
  implements IBarcodeImporterProcessingRepo
{
  uploadFile = (
    draft: ProcessGeneral,
    file: File,
    data?: Record<string, any>
  ): ApiResponseAsync<void> => {
    const link = draft._links?.['uploadBarcodeImporterBatch'];
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "uploadBarcodeImporterBatch" is not available in process draft'
          )
        )
      );
    }

    const fileFormData = new FormData();
    fileFormData.append(UPLOAD_FILE_FIELD_NAME, file);

    if (data) fileFormData.append('properties', JSON.stringify(data));

    return this.helpers.handlePost(link.href, fileFormData);
  };

  searchLogs = (
    draft: ProcessGeneral,
    query: SearchRequestTyped<SearchableBarcodeImporterLog>
  ): ApiResponseAsync<BarcodeImporterLog[]> => {
    const link = draft._links?.['searchBarcodeImporterLogs'];
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "searchBarcodeImporterLogs" is not available in process draft'
          )
        )
      );
    }

    return this.helpers.executeSearch(link, query);
  };

  getErrorBatches = (
    draft: ProcessGeneral
  ): ApiResponseAsync<DailyBarcodeImporterBatches[]> => {
    const link = draft._links?.['getBarcodeImporterErrorBatches'];
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "getBarcodeImporterErrorBatches" is not available in process draft'
          )
        )
      );
    }

    return this.helpers
      .handleGet<DailyBarcodeImporterBatches[]>(link.href)
      .then((result) => {
        if (isApiError(result)) return result;

        return result.map((value) => ({
          ...value,
          date: new Date(value.date),
        }));
      });
  };

  reprocessErrorBatch = (
    batch: BarcodeImporterBatch
  ): ApiResponseAsync<void> => {
    const link = batch._links?.['reprocessError'];
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "reprocessError" is not available in Barcode Import batch'
          )
        )
      );
    }

    return this.helpers.handlePost(link.href, {});
  };

  getDownloadErrorFileLink(
    batch: BarcodeImporterBatch
  ): ApiResponseAsync<Link> {
    const link = batch._links?.['getErrorFile'];
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "getErrorFile" is not available in Barcode Import batch'
          )
        )
      );
    }

    return Promise.resolve(link);
  }

  searchBarcodeImporterProcessInstances = (
    draft: ProcessGeneral,
    query: SearchRequestTyped<BarcodeImporterProcessInstance>
  ): ApiResponseAsync<BarcodeImporterProcessInstance[]> => {
    const link = draft._links?.['searchBarcodeImporterInstances'];
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "searchBarcodeImporterInstances" is not available in process draft'
          )
        )
      );
    }

    return this.helpers.executeSearch(link, query);
  };
}
